/**
 * @file CompanyProfilePage.tsx
 * @brief Company Admin → company profile (org record: name, billing email,
 *        and address).
 *
 * @author DES
 * @date 29-06-2026
 * @updated 29-07-2026 — GridBlock flush layout
 */

import { SkeletonFields } from "@/components/ui/Skeleton";
import { useEffect, useState } from "react";
import { Building2, LoaderCircle, Mail, Save } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import {
  useCompanyBillingProfile,
  useUpdateCompanyBillingProfile,
} from "@/features/company-billing/api";
import {
  CompanyBillingAddressFields,
  type BillingAddressFormValues,
} from "@/features/company-billing/components/CompanyBillingAddressFields";

const EMPTY_ADDRESS: BillingAddressFormValues = {
  address: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
};

export function CompanyProfilePage() {
  const { data: profile, isLoading, isError } = useCompanyBillingProfile();
  const updateProfile = useUpdateCompanyBillingProfile();

  const [billingEmail, setBillingEmail] = useState("");
  const [address, setAddress] = useState<BillingAddressFormValues>(EMPTY_ADDRESS);

  useEffect(() => {
    if (!profile) return;
    setBillingEmail(profile.billing_email ?? "");
    setAddress({
      address: profile.address ?? "",
      city: profile.city ?? "",
      state: profile.state ?? "",
      postal_code: profile.postal_code ?? "",
      country: profile.country ?? "",
    });
  }, [profile]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfile.mutateAsync({
        billing_email: billingEmail.trim() || undefined,
        address: address.address.trim() || undefined,
        city: address.city.trim() || undefined,
        state: address.state.trim() || undefined,
        postal_code: address.postal_code.trim() || undefined,
        country: address.country.trim() || undefined,
      });
      toast({ variant: "success", title: "Company profile updated" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">
            Company profile
          </h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Your organisation&apos;s billing email and address, used on invoices
            and corporate cards.
          </p>
        </GridBand>

        {isLoading ? (
          <GridCell>
            <SkeletonFields fields={5} className="py-4" />
          </GridCell>
        ) : isError ? (
          <GridCell>
            <p className="py-10 text-center text-sm text-destructive">
              Couldn&apos;t load your company profile. Please refresh and try
              again.
            </p>
          </GridCell>
        ) : (
          <form onSubmit={handleSave}>
            <GridBand className="text-sm font-medium">
              Organisation details
              <span className="ml-1 font-normal text-muted-foreground">
                · Company name is managed by your super admin.
              </span>
            </GridBand>

            <GridCells className="grid-cols-1 sm:grid-cols-2">
              <GridCell dense className="border-0">
                <FormFieldGroup label="Company name" htmlFor="company-name" icon={<Building2 />}>
                  <Input
                    capitalizeFirst
                    id="company-name"
                    value={profile?.name ?? ""}
                    disabled
                    readOnly
                  />
                </FormFieldGroup>
              </GridCell>
              <GridCell dense className="border-0">
                <FormFieldGroup label="Billing email" htmlFor="billing-email" icon={<Mail />}>
                  <Input
                    id="billing-email"
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    placeholder="accounts@company.com"
                  />
                </FormFieldGroup>
              </GridCell>
            </GridCells>

            <GridBand className="text-sm font-medium">Billing address</GridBand>
            <GridCell className="border-0">
              <CompanyBillingAddressFields
                values={address}
                onChange={setAddress}
                disabled={updateProfile.isPending}
                flush
                icons
              />
            </GridCell>

            <GridCell padded={false} className="border-b-0">
              <div className="flex justify-end px-4 py-3 md:px-5">
                <Button
                  type="submit"
                  disabled={updateProfile.isPending}
                  className="gap-2"
                >
                  {updateProfile.isPending ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save changes
                </Button>
              </div>
            </GridCell>
          </form>
        )}
      </GridBlock>
    </div>
  );
}
